/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from "react";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { createPortal } from "react-dom";
import { IconBarcode, IconDownload, IconTrash } from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import ListView from "../../../components/common/ListView";
import {
  useFetchShipmentInventoryDataByLocation,
  useLocationBarcodeVerifyMutation,
  useShipmentInventoryAddMutation,
  useShipmentInventoryDeleteMutation,
  useShipmentInventoryVerifyMutation,
} from "../../../hooks/inventoryHooks";
import toast from "react-hot-toast";
import MessageModal from "../../../components/MessageModal";
import { BarcodeInput } from "./BarcodeInput";
import NoDataFound from "../../../components/common/NoDataFound";
import { useLanguage, T } from "../../../context/LanguageContext";

export const useBarcodeScanner = (onScan: (code: string) => void) => {
  useEffect(() => {
    let buffer = "";
    let lastKeyTime = 0;
    let timeout: NodeJS.Timeout;

    const handleKeydown = (e: KeyboardEvent) => {
      const now = Date.now();

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();

        if (buffer.length > 0) {
          onScan(buffer.trim());
          buffer = "";
        }
        return;
      }

      // ✅ Allow all printable characters (not just alphanumeric)
      if (/^[\x20-\x7E]$/.test(e.key)) {
        if (now - lastKeyTime > 200) buffer = "";
        buffer += e.key;
        lastKeyTime = now;

        clearTimeout(timeout);
        timeout = setTimeout(() => (buffer = ""), 500);
      }
    };

    window.addEventListener("keydown", handleKeydown, true);

    return () => {
      window.removeEventListener("keydown", handleKeydown, true);
      clearTimeout(timeout);
    };
  }, [onScan]);
};

export default function InventoryCheckTab({ 
  searchQuery,
  portalContainer
}: { 
  searchQuery?: string; 
  portalContainer?: HTMLDivElement | null;
}) {
  const { t } = useLanguage();
  const { allowed: canCreateShipment } = usePermission("shipment_inventory", "create");
  const { allowed: canUpdateShipment } = usePermission("shipment_inventory", "update");
  const { allowed: canDeleteShipment } = usePermission("shipment_inventory", "delete");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [localSearch, setLocalSearch] = useState<string>("");
  const searchFor = searchQuery !== undefined ? searchQuery : localSearch;
  const setSearchFor = searchQuery !== undefined ? () => {} : setLocalSearch;
  const [locationId, setLocationId] = useState<string>("");
  const [locationShowId, setLocationShowId] = useState<string>("");
  const [awbNumber, setAwbNumber] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isShelfVerified, setIsShelfVerified] = useState(false);

  const [openDelete, setOpenDelete] = useState(false);
  const [open, setOpen] = useState(false);

  const { data, refetch } = useFetchShipmentInventoryDataByLocation(
    locationId || "",
    pageNo,
    limit,
    searchFor,
  );

  const { mutate: verifyShipmentInventory } =
    useShipmentInventoryVerifyMutation();
  const { mutate: deleteShipmentInventory } =
    useShipmentInventoryDeleteMutation();
  const { mutate: addShipmentInventory } = useShipmentInventoryAddMutation();
  const { mutate: verifyLocationBarcode } = useLocationBarcodeVerifyMutation();

  useEffect(() => {
    if (locationId) {
      refetch();
    }
  }, [locationId]);

  const resetShelf = () => {
    setLocationShowId("");
    setLocationId("");
    setIsShelfVerified(false);
  };

  useBarcodeScanner(async (barcode) => {
    if (isProcessing) return;
    console.log("📦 Scanned:", barcode);

    setIsProcessing(true);

    try {
      if (!locationId) {
        const verifiedLocationId: any = await handleVerifyBarcode(barcode);

        if (!verifiedLocationId) {
          // toast.error("Invalid or unverified location barcode");
          return;
        } else {
          setLocationId(verifiedLocationId);
          toast.success(`${t("Location set")}: ${verifiedLocationId}`);
          return;
        }
      }
      await handleSendRequest(barcode);
    } catch (error) {
      console.error("⚠️ Error handling barcode:", error);
      toast.error(t("Failed to process barcode"));
    } finally {
      setIsProcessing(false);
    }
  });

  const handleSendRequest = (awb: string) => {
    if (!canUpdateShipment) {
      toast.error(t("You do not have permission to update shipment inventory."));
      return;
    }
    verifyShipmentInventory(
      { awb, locationId },
      {
        onSuccess: (res) => {
          toast.success(t(res.message) || res.message);
          refetch();
          if (res.message === "Unmatched") {
            setOpen(true);
            setAwbNumber(awb);
          }
        },
        onError: (error) => {
          console.error("Error:", error);
          toast.error(String(error));
        },
      },
    );
  };

  const handleVerifyBarcode = (barcode: string) => {
    verifyLocationBarcode(
      {
        barcodeId: barcode,
      },
      {
        onSuccess: (res: any) => {
          refetch();
          setLocationShowId(res?.barcodeLabel || (typeof res === "string" ? res : ""));
          setLocationId(barcode);
          setIsShelfVerified(true);
        },
        onError: (error) => {
          console.error("Error:", error);
          toast.error(String(error));
        },
      },
    );
  };

  const handleDeleteFloor = () => {
    if (!canDeleteShipment) {
      toast.error(t("You do not have permission to delete shipment inventory."));
      return;
    }
    deleteShipmentInventory(
      {
        awb: awbNumber,
        locationId,
      },
      {
        onSuccess: () => {
          toast.success(t("Unmatched shipment deleted successfully!"));
          setOpenDelete(false);
          refetch();
        },
        onError: (error: any) =>
          toast.error(`${t("Failed to delete shipment")}: ${error}`),
      },
    );
  };

  const handleAddRequest = () => {
    if (!canCreateShipment) {
      toast.error(t("You do not have permission to create shipment inventory."));
      return;
    }
    addShipmentInventory(
      {
        locationId: locationId,
        orderId: [awbNumber],
      },
      {
        onSuccess: () => {
          toast.success(t("Shipment Inventory added successfully"));
          setOpen(false);
          refetch();
        },
        onError: (error) => {
          console.log(error, "error");
          toast.error(`${error}`);
        },
      },
    );
  };

  const actionControls = (
    <>
      {isShelfVerified ? (
        <button
          onClick={resetShelf}
          className="px-3.5 py-1.5 bg-[#FBD037] hover:bg-[#e2bd30] border border-[#FBD037] text-[#131327] rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150 text-xs font-bold flex items-center gap-1"
        >
          <IconBarcode size={16} />
          <T>Reset Shelf</T>
        </button>
      ) : (
        <BarcodeInput
          placeholder={t("Scan / paste shelve barcode")}
          onVerified={handleVerifyBarcode}
          disabled={isProcessing || isShelfVerified}
        />
      )}

      {searchQuery === undefined && (
        <NormalSearch
          value={searchFor}
          onChange={(e) => {
            setSearchFor(e.target.value);
            setPageNo(1);
          }}
        />
      )}

      {/* Export/Download */}
      <button
        className="flex items-center justify-center p-1.5 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
        title={t("Export")}
      >
        <IconDownload size={17} stroke={2.5} />
      </button>
    </>
  );

  return (
    <>
      {portalContainer && createPortal(actionControls, portalContainer)}

      {/* ── Main Card (Header + Table Joined) ─────────────────────── */}
      <div
        className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-visible mb-6"
        style={{ minHeight: "150px" }}
      >
        {locationShowId && (
          <div className="px-6 py-3.5 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-3">
              <div className="bg-[#FBD037] text-[#131327] font-black text-sm px-3.5 py-1.5 rounded-xl tracking-wider shadow-sm select-none">
                {t("inventory check (shelve:")} {locationShowId})
              </div>
            </div>
          </div>
        )}

        {/* Unified Table Container (Joined) */}
        <div className="p-6 bg-[#FCFDF8] rounded-b-3xl">
          <div className="grid grid-cols-2 gap-6">
            {data?.inventoryMovement?.length > 0 ? (
              <ListView
                mailDivClassName="w-full rounded-3xl"
                data={data?.inventoryMovement?.filter(
                  (inventory: any) => !inventory?.isVerify,
                )}
                onRowClick={(client) => console.log("Clicked:", client)}
                columns={[
                  { label: t("#"), render: (_, i) => i + 1 },
                  {
                    label: t("AWB"),
                    render: (c: any) => c.awb,
                  },
                  {
                    label: t("Shelf ID"),
                    render: (c: any) => c.locationName,
                  },
                  {
                    label: t("Status"),
                    render: (c: any) => {
                      // const isAssigned = c.isVerify;
                      const statusClasses = c.isVerify
                        ? "bg-[#FF4848] text-[#ffffff]"
                        : "bg-[#20BF55] text-[#ffffff]";
                      return (
                        <div className="flex items-center gap-2">
                          <button
                            disabled={!canUpdateShipment}
                            onClick={() => handleSendRequest(c?.awb)}
                            className={`rounded-full px-4 py-1 text-center text-xs font-semibold ${statusClasses} ${!canUpdateShipment ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {c.isVerify ? t("Matched") : t("Match")}
                          </button>
                          {canDeleteShipment && (
                            <button
                              className="p-1.5 rounded-full hover:bg-gray-100 hover:text-[#212121] text-slate-500 transition flex items-center justify-center cursor-pointer"
                              onClick={() => {
                                setOpenDelete(true);
                                setAwbNumber(c.awb);
                              }}
                            >
                              <IconTrash strokeWidth={1.5} size={18} />
                            </button>
                          )}
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : (
              <NoDataFound />
            )}
            {data?.inventoryMovement?.filter(
              (inventory: any) => inventory?.isVerify,
            )?.length > 0 ? (
              <ListView
                mailDivClassName="w-full rounded-3xl"
                data={data?.inventoryMovement?.filter(
                  (inventory: any) => inventory?.isVerify,
                )}
                onRowClick={(client) => console.log("Clicked:", client)}
                columns={[
                  {
                    label: t("AWB"),
                    render: (c: any) => c.awb,
                  },
                ]}
              />
            ) : (
              <NoDataFound />
            )}
          </div>
        </div>
      </div>
        <MessageModal
          open={openDelete}
          onClose={() => setOpenDelete(false)}
          title={t("Delete Inventory Shipment Confirmation!")}
          subtitle={t("Are you sure you want to perform this action?")}
          img={"/img/junk.png"}
          showFooter={true}
          closeButtonText={t("Cancel")}
          actionButtonText={t("Confirm")}
          actionButtonOnClick={handleDeleteFloor}
          size="md"
        />
        <MessageModal
          open={open}
          onClose={() => setOpen(false)}
          title={t("Update Shipment Inventory Confirmation!")}
          subtitle={`${t("are you sure you want to add shipment in given location")} ${locationShowId} ?`}
          img={"/img/junk.png"}
          showFooter={true}
          closeButtonText={t("Cancel")}
          actionButtonText={t("Confirm")}
          actionButtonOnClick={handleAddRequest}
          size="md"
        />
      </>
    );
  }
